/**
 * BlogPost Page
 * Renders an individual blog post from the blogPosts data file.
 * Includes a lightweight Markdown → JSX renderer (no external deps).
 * Public route — no auth required.
 */

import React, { useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Divider,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getPostBySlug, blogPosts } from '../data/blogPosts';

/* ─────────────────────────────────────────────────────────────────────────────
   Inline markdown renderer — handles **bold**, [link](url), `code`
───────────────────────────────────────────────────────────────────────────── */
function renderInline(text) {
  // Split on inline patterns: `code`, **bold**, [text](url)
  const parts = text.split(/(`[^`]+`|\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);
  return parts.map((part, i) => {
    // Inline code
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      return (
        <code
          key={i}
          style={{
            background: 'rgba(107, 70, 193, 0.08)',
            padding: '2px 6px',
            borderRadius: 4,
            fontFamily: 'monospace',
            fontSize: '0.88em',
            color: '#6B46C1',
          }}
        >
          {part.slice(1, -1)}
        </code>
      );
    }
    // Bold
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    // Link [text](url)
    const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) {
      return (
        <a
          key={i}
          href={linkMatch[2]}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#6B46C1', textDecoration: 'underline', fontWeight: 500 }}
        >
          {linkMatch[1]}
        </a>
      );
    }
    return part;
  });
}

/* ─────────────────────────────────────────────────────────────────────────────
   Block parser — converts Markdown string → array of typed block objects
───────────────────────────────────────────────────────────────────────────── */
function parseBlocks(content) {
  const lines = content.split('\n');
  const blocks = [];
  let paragraphLines = [];
  let listItems = [];
  let listOrdered = false;

  const flushParagraph = () => {
    if (paragraphLines.length > 0) {
      blocks.push({ type: 'p', lines: [...paragraphLines] });
      paragraphLines = [];
    }
  };

  const flushList = () => {
    if (listItems.length > 0) {
      blocks.push({ type: listOrdered ? 'ol' : 'ul', items: [...listItems] });
      listItems = [];
    }
  };

  for (const line of lines) {
    const trimmed = line.trim();

    if (trimmed === '') {
      flushParagraph();
      flushList();
    } else if (trimmed === '---') {
      flushParagraph();
      flushList();
      blocks.push({ type: 'hr' });
    } else if (trimmed.match(/^#{1,6} /)) {
      flushParagraph();
      flushList();
      const level = trimmed.match(/^(#{1,6}) /)[1].length;
      const text = trimmed.replace(/^#{1,6} /, '');
      blocks.push({ type: `h${level}`, text });
    } else if (trimmed.startsWith('> ')) {
      flushParagraph();
      flushList();
      blocks.push({ type: 'blockquote', text: trimmed.slice(2) });
    } else if (trimmed.match(/^[-*] /)) {
      flushParagraph();
      if (listItems.length > 0 && listOrdered) flushList();
      listOrdered = false;
      listItems.push(trimmed.replace(/^[-*] /, ''));
    } else if (trimmed.match(/^\d+\. /)) {
      flushParagraph();
      if (listItems.length > 0 && !listOrdered) flushList();
      listOrdered = true;
      listItems.push(trimmed.replace(/^\d+\. /, ''));
    } else {
      flushList();
      paragraphLines.push(trimmed);
    }
  }

  flushParagraph();
  flushList();
  return blocks;
}

/* ─────────────────────────────────────────────────────────────────────────────
   Block renderer — converts block objects → React elements
───────────────────────────────────────────────────────────────────────────── */
function renderBlock(block, i) {
  const headingSx = {
    h1: { variant: 'h3', mt: 6, mb: 2, fontWeight: 800 },
    h2: { variant: 'h4', mt: 5, mb: 2, fontWeight: 700 },
    h3: { variant: 'h5', mt: 4, mb: 1.5, fontWeight: 700 },
    h4: { variant: 'h6', mt: 3, mb: 1, fontWeight: 700 },
    h5: { variant: 'subtitle1', mt: 2, mb: 0.5, fontWeight: 700 },
    h6: { variant: 'subtitle2', mt: 2, mb: 0.5, fontWeight: 700 },
  };

  if (headingSx[block.type]) {
    const { variant, ...sx } = headingSx[block.type];
    return (
      <Typography key={i} variant={variant} sx={{ color: '#1a1a2e', ...sx }}>
        {renderInline(block.text)}
      </Typography>
    );
  }

  switch (block.type) {
    case 'hr':
      return <Divider key={i} sx={{ my: 4 }} />;

    case 'blockquote':
      return (
        <Box
          key={i}
          sx={{
            borderLeft: '4px solid #8B5CF6',
            pl: 3,
            py: 1.5,
            my: 3,
            bgcolor: 'rgba(139, 92, 246, 0.05)',
            borderRadius: '0 8px 8px 0',
          }}
        >
          <Typography
            variant="body1"
            sx={{ fontStyle: 'italic', lineHeight: 1.8, color: '#374151' }}
          >
            {renderInline(block.text)}
          </Typography>
        </Box>
      );

    case 'p':
      return (
        <Typography
          key={i}
          variant="body1"
          paragraph
          sx={{
            whiteSpace: 'pre-line',
            lineHeight: 1.85,
            fontSize: '1.05rem',
            color: '#374151',
          }}
        >
          {renderInline(block.lines.join('\n'))}
        </Typography>
      );

    case 'ul':
      return (
        <Box
          key={i}
          component="ul"
          sx={{ pl: 3, mb: 2, '& li': { mb: 0.75 } }}
        >
          {block.items.map((item, j) => (
            <Box
              key={j}
              component="li"
              sx={{ fontSize: '1.05rem', lineHeight: 1.8, color: '#374151' }}
            >
              {renderInline(item)}
            </Box>
          ))}
        </Box>
      );

    case 'ol':
      return (
        <Box
          key={i}
          component="ol"
          sx={{ pl: 3, mb: 2, '& li': { mb: 0.75 } }}
        >
          {block.items.map((item, j) => (
            <Box
              key={j}
              component="li"
              sx={{ fontSize: '1.05rem', lineHeight: 1.8, color: '#374151' }}
            >
              {renderInline(item)}
            </Box>
          ))}
        </Box>
      );

    default:
      return null;
  }
}

/* ─────────────────────────────────────────────────────────────────────────────
   BlogPost Component
───────────────────────────────────────────────────────────────────────────── */
const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = useMemo(() => getPostBySlug(slug), [slug]);

  // Set page title + meta description for SEO
  useEffect(() => {
    if (post) {
      document.title = `${post.title} | WordExPress Blog`;
      // Update or create meta description
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', 'description');
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', post.excerpt);
    }
  }, [post]);

  // Always call hooks before any early returns
  const blocks = useMemo(() => parseBlocks(post ? post.content : ''), [post]);

  // Pick a couple of other posts to suggest
  const related = blogPosts.filter((p) => p.slug !== slug).slice(0, 2);

  // 404 handling
  if (!post) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Post not found
          </Typography>
          <Button variant="contained" onClick={() => navigate('/blog')}>
            Back to Blog
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f9fafb' }}>
      {/* Post Hero */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #6B46C1 0%, #553C9A 100%)',
          pt: { xs: 6, md: 8 },
          pb: { xs: 8, md: 10 },
        }}
      >
        <Container maxWidth="md">
          {/* Back link */}
          <Button
            startIcon={<ArrowBackIcon />}
            component={Link}
            to="/blog"
            sx={{
              color: 'rgba(255,255,255,0.8)',
              mb: 4,
              pl: 0,
              fontWeight: 600,
              '&:hover': { color: '#fff', background: 'transparent' },
            }}
          >
            All Posts
          </Button>

          {/* Title */}
          <Typography
            variant="h3"
            component="h1"
            fontWeight={800}
            sx={{
              color: '#fff',
              lineHeight: 1.25,
              mb: 3,
              fontSize: { xs: '1.75rem', md: '2.5rem' },
            }}
          >
            {post.title}
          </Typography>

          {/* Meta chips */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip
              icon={<CalendarTodayIcon style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14 }} />}
              label={post.date}
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 600 }}
            />
            <Chip
              icon={<PersonIcon style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14 }} />}
              label={post.author}
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 600 }}
            />
            <Chip
              icon={<AccessTimeIcon style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14 }} />}
              label={post.readTime}
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 600 }}
            />
          </Box>
        </Container>
      </Box>

      {/* Post Body */}
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: 3,
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            p: { xs: 3, md: 6 },
          }}
        >
          {blocks.map((block, i) => renderBlock(block, i))}
        </Box>

        {/* CTA Banner */}
        <Box
          sx={{
            mt: 6,
            p: { xs: 4, md: 5 },
            background: 'linear-gradient(135deg, #6B46C1 0%, #8B5CF6 100%)',
            borderRadius: 3,
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" fontWeight={800} sx={{ color: '#fff', mb: 1 }}>
            Generate your WordPress theme in 5 minutes
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.85)', mb: 3 }}>
            Describe your site. Download a complete, installable theme. One-time payment.
          </Typography>
          <Button
            variant="contained"
            size="large"
            href="https://getwordexpress.com"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              bgcolor: '#fff',
              color: '#6B46C1',
              fontWeight: 700,
              px: 4,
              py: 1.5,
              borderRadius: 2,
              '&:hover': { bgcolor: 'grey.100' },
            }}
          >
            Try WordExPress
          </Button>
        </Box>

        {/* Related Posts */}
        {related.length > 0 && (
          <Box sx={{ mt: 8 }}>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 3, color: '#1a1a2e' }}>
              More Posts
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {related.map((p) => (
                <Box
                  key={p.id}
                  onClick={() => navigate(`/blog/${p.slug}`)}
                  sx={{
                    p: 3,
                    bgcolor: '#fff',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: '0 4px 20px rgba(107, 70, 193, 0.1)' },
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {p.date} · {p.readTime}
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1a1a2e' }}>
                    {p.title}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Back to Blog */}
        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            component={Link}
            to="/blog"
            sx={{ color: '#6B46C1', fontWeight: 700 }}
          >
            Back to Blog
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default BlogPost;
